import { Helmet } from "react-helmet"
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  tooltipClasses,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material"
import {
  useNavigate,
  useParams,
} from "react-router-dom"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { makeStyles, styled } from "@mui/styles"
import { find } from 'lodash';

import cssVariables from "../../../../theme/cssVariables"

import ConfirmInboxDialog from "../../../../components/inbox/ConfirmDialog";
import ConfirmDialog from "../../../../components/ConfirmDialog"
import MessageDialog from "../../../../components/MessageDialog"
import DetailDialog from "../../../../components/calibration/DetailDialog"
import UploadDialog from "../../../../components/calibration/DialogUpload"

import { ReactComponent as ImportIcon } from "../../../../assets/icons/import.svg"
import { ReactComponent as Download } from "../../../../assets/icons/download.svg";
import { ReactComponent as FloppyDiskIcon } from "../../../../icons/floppy-disk.svg"
import { ReactComponent as ArrowLeftIcon } from "../../../../icons/arrow-left.svg"
import { ReactComponent as CompassIcon } from "../../../../icons/compass.svg"
import { ReactComponent as PeopleGroupIcon } from "../../../../icons/people-group.svg"
import { ReactComponent as StarsGroupIcon } from "../../../../icons/stars-group.svg"
import { ReactComponent as StarsGroupInactiveIcon } from "../../../../icons/stars-group-inactive.svg"
import { ReactComponent as DartsIcon } from "../../../../icons/darts.svg"
import { ReactComponent as CalibrationAlertIcon } from "../../../../icons/danger.svg"
import { ReactComponent as DocumentIcon } from "../../../../icons/document.svg"
import { ReactComponent as StarIcon } from "../../../../icons/star.svg"
import { ReactComponent as MarkIcon } from "../../../../assets/icons/mark.svg";
import { ReactComponent as CheckIcon } from "../../../../assets/icons/check-2.svg";

import ApexCharts from "react-apexcharts"

import { getCalibrationDetail, getEmployeeCalibrationDetail, postSaveCalibration, postSubmitCalibration, downloadCalibrationData, postWorkflowSubmit, postWorkflowReject } from "../../../../actions/calibration"

import * as Util from "../../../../services/global.service"

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#343A40",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#343A40",
    color: "#FFFFFF",
    padding: "11px 15px",
  },
}))

const statusBox = (data, isCenter = false) => {
  let fontColor = "#404349"
  let backgroundColor = "#A0A0A0"
  let text = "-"
  if (data === "in_progress") {
    fontColor = "#EFB76D"
    backgroundColor = "rgba(239, 183, 109, 0.1)"
    text = "In Progress"
  } else if (data === "pending") {
    fontColor = "#F28E2B"
    backgroundColor = "rgba(242, 142, 43, 0.1)"
    text = "Pending"
  } else if (data === "approved" || data === "completed") {
    fontColor = "#5DA196"
    backgroundColor = "rgba(93, 161, 150, 0.1)"
    text = "Completed"
  }

  return (
    <Box sx={{ backgroundColor: backgroundColor ?? "#fff", width: '120px', paddingTop: '8px', paddingBottom: '8px', height: '40px', textAlign: 'center', borderRadius: '5px', margin: isCenter ? '0 auto' : 'none', width: "130px", height: "40px" }}>
      <Typography fontSize={16} lineHeight="21px" fontWeight="500" color={fontColor}>
        {Util.upperFirstWord(text)}
      </Typography>
    </Box>
  )
}

const dateTimeFormat = (data) => {
  const language = localStorage.getItem("lang");
  const filterLanguage = language === 'th-TH' ? 'th-TH' : language === "en-US" ? "en-GB" : language
  if (data !== undefined && data !== null && data !== "") {
    const date = new Date(data.replace(/-/g, "/").replace(/T/g, " "))
    const dateTH = date.toLocaleDateString(filterLanguage === undefined || filterLanguage === null || filterLanguage === "" ? 'th-TH' : filterLanguage, {
      year: '2-digit',
      month: 'short',
      day: filterLanguage === 'th-TH' ? 'numeric' : '2-digit',
    })
    const time = date.toLocaleTimeString(filterLanguage === undefined || filterLanguage === null || filterLanguage === "" ? 'th-TH' : filterLanguage, { hour: '2-digit', minute: '2-digit' })
    return `${filterLanguage === undefined || filterLanguage === null || filterLanguage === "" ? `${dateTH} ,${time} น.` : `${filterLanguage === 'th-TH' ? `${dateTH} ,${time} น.` : `${dateTH} ,${time}`}`}`
  }
}

export const SCORETYPE = {
  RATING: 'rating',
  PERCENTAGE: 'percentage',
}

// main component
const EditCalibration = (props) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const { serial } = useParams()

  const calibrationState = useSelector((state) => state.Calibration)

  // scroll table
  const [canEventFire, setCanEventFire] = React.useState(true)
  useEffect(() => {
    const slider = document.querySelector('#calibration-table')
    let isDown = false
    let startX
    let scrollLeft
    let isClicked = true
    const mouseDown = (e) => {
      isClicked = true
      isDown = true
      startX = e.pageX - slider.offsetLeft
      scrollLeft = slider.scrollLeft
    }

    const mouseLeave = () => {
      isDown = false
    }

    const mouseUp = () => {
      if (!isClicked) {
        setCanEventFire(false)
      } else {
        setCanEventFire(true)
      }
      isClicked = true
      isDown = false
    }

    const mouseMove = (e) => {
      isClicked = false
      if (!isDown) return
      const x = e.pageX - slider.offsetLeft
      const scrollSpeed = (x - startX) * 1 //scroll-speed
      slider.scrollLeft = scrollLeft - scrollSpeed
    }
    slider.addEventListener('mousedown', mouseDown)
    slider.addEventListener('mouseleave', mouseLeave)
    slider.addEventListener('mouseup', mouseUp)
    slider.addEventListener('mousemove', mouseMove)

    return () => {
      slider.removeEventListener('mousedown', mouseDown)
      slider.removeEventListener('mouseleave', mouseLeave)
      slider.removeEventListener('mouseup', mouseUp)
      slider.removeEventListener('mousemove', mouseMove)
    }
  }, [])
  // end scroll table

  const useStyles = makeStyles({
    primaryColorSvg: {
      "& > path": {
        stroke: cssVariables.color.primary,
      }
    },
    starIconSvg: {
      width: "20px",
      height: "20px"
    },
    clickable: {
      cursor: "pointer",
      transition:
        "color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      "&:hover": {
        color: "rgb(65, 112, 105) !important",
      },
    },
    boxStyled: {
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)!important",
      borderRadius: "10px!important"
    },
    calibrationCardRoot: {
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)!important",
      borderRadius: "5px!important",
      width: "268px",
      height: "812px",
      overflow: "auto",
      padding: "15px",
      flexShrink: 0
    },
    calibrationAlert: {
      backgroundColor: "#CC6058!important",
      borderRadius: "4px",
      height: "40px",
      padding: "10px 7px",
      boxShadow: "none!important",
      display: "flex",
      alignItems: "center"
    },
    calibrationItemCard: {
      borderRadius: "5px",
      height: "180px",
      padding: "11px 12px",
      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.1)!important",
      display: "flex",
      flexDirection: "column"
    },
    calibrationItemGrid: {
      borderRadius: "5px",
      cursor: "pointer",
      "&:hover": {
        outline: `1px solid ${cssVariables.color.primary}`
      }
    },
    btnDisabled: {
      backgroundColor: "#AAAAAA!important",
      color: "#FFF!important",
      border: "none!important"
    }
  })
  const classes = useStyles()
  const styles = {
    link: {
      color: "#5DA196",
      cursor: "pointer",
    },
    end: {
      color: "#A3A3A3",
    },
  }
  // confirm calibration dialog & change calibration value on detail card
  const [isOpenChangeCalibrationDialog, setIsOpenChangeCalibrationDialog] = React.useState(false)
  const [isConfirmChangeCalibration, setIsConfirmChangeCalibration] = React.useState(false)

  const [isOpenDiscardDialog, setIsOpenDiscardDialog] = React.useState(false)
  const [isOpenSaveDialog, setIsOpenSaveDialog] = React.useState(false)
  const [isOpenSubmitDialog, setIsOpenSubmitDialog] = React.useState(false)
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = React.useState(false)
  const [successDialogMessage, setSuccessDialogMessage] = React.useState("")
  const [versions, setVersions] = React.useState([])
  const [selectedCurrentVersion, setSelectedCurrentVersion] = React.useState(null)
  const [selectedVersion, setSelectedVersion] = React.useState(null)
  const [openUpload, setOpenUpload] = React.useState(false)

  const handleChangeCalibrationDialog = (value) => {
    setIsOpenChangeCalibrationDialog(value)
    setIsConfirmChangeCalibration(false)
  }
  const handleConfirmChangeClassificationDropdown = () => {
    setIsOpenChangeCalibrationDialog(false)
    setIsConfirmChangeCalibration(true)
  }
  const handleConfirmDiscard = () => {
    setIsOpenDiscardDialog(false)
    goTo("/kpi/calibration")
  }

  const handleConfirmSave = () => {
    setIsOpenSaveDialog(false)
    handleSaveCalibration()
  }

  const handleConfirmSubmit = () => {
    setIsOpenSubmitDialog(false)
    handleSubmitCalibration()
  }


  const navigate = useNavigate()
  const goTo = (data) => {
    navigate(data, { replace: false })
  }

  const handleBack = () => {
    if (dataModified) {
      setIsOpenDiscardDialog(true)
    } else {
      goTo("/kpi/calibration")
    }
  }

  const handleSaveCalibration = () => {
    let postData = {
      calibration_session_id: calibrationSessionData?.serial ?? "",
      employee_distribution: calibrationState.changedCalibrationData,
      version: selectedVersion?.id,
    }

    //console.log('handleSaveCalibration', postData); return;

    dispatch(postSaveCalibration(postData)).then(() => {
      fetchAndSetData(true)
      setIsOpenSuccessDialog(true)
      setSuccessDialogMessage(t("calibration.label.dialog.saveSuccessText"))
    })
  }

  const handleSubmitCalibration = () => {
    let postData = { calibration_session_id: calibrationSessionData?.serial ?? "" }
    dispatch(postSubmitCalibration(postData)).then(() => {
      fetchAndSetData(true)
      setIsOpenSuccessDialog(true)
      setSuccessDialogMessage(t("calibration.label.dialog.submitSuccessText"))
    })
  }

  const [isDetailDialogOpen, setIsDetailDialogOpen] = React.useState(false)
  const [selectedDetailEmployeeData, setSelectedDetailEmployeeData] = React.useState({})
  const handleDetailDialog = (data, value) => {
    if (canEventFire) {
      dispatch(getEmployeeCalibrationDetail(serial, data?.serial ?? "")).then(resp => {
        setSelectedDetailEmployeeData(resp)
        setIsDetailDialogOpen(value)
      })
    }
  }

  const [cycleData, setCycleData] = React.useState({})
  const [summaryData, setSummaryData] = React.useState({
    total: 0,
    withRating: 0,
    noRating: 0
  })

  // chart data
  let chartOptions = {
    series: [{
      name: t("calibration.label.actual"),
      type: 'column',
      data: [0]
    }, {
      name: t("calibration.label.guideline"),
      type: 'line',
      data: [0]
    }],
    options: {
      noData: {
        text: "No Data",
        align: 'center',
        verticalAlign: 'middle',
      },
      chart: {
        type: "line",
        animations: {
          enabled: false
        },
        toolbar: {
          show: false,
        },
        selection: {
          enabled: false
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        width: 2
      },
      dataLabels: {
        enabled: false,
        enabledOnSeries: false
      },
      colors: ["#5DA196", "#F19562"],
      markers: {
        size: 10,
        strokeWidth: 0,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 5,
          columnWidth: "25%",
        },
      },
      grid: {
        show: true,
      },
      title: {
        show: false,
      },
      xaxis: {
        axisBorder: {
          show: true,
          color: '#AAAAAA',
          height: 2,
          width: '100%',
          offsetX: 0,
          offsetY: 0
        },
        categories: [],
        labels: {
          formatter: function (val) {
            return val
          },
        },
        axisTicks: {
          show: false,
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: [{
        title: {
          text: undefined,
        },
        axisBorder: {
          show: true,
          color: '#AAAAAA',
          width: 2,
          offsetX: 3,
          offsetY: 0
        },
      }],
      tooltip: {
        enabled: true,
        enabledOnSeries: undefined,
        shared: true,
        followCursor: false,
        intersect: false,
        inverseOrder: false,
        custom: undefined,
        fillSeriesColor: false,
        style: {
          fontSize: "16px",
          fontFamily: "Kanit",
          fontWeight: 300,
        },
        marker: {
          show: false
        },
        onDatasetHover: {
          highlightDataSeries: false,
        },
        x: {
          show: false
        },
        y: {
          formatter: undefined,
          title: {
            formatter: (seriesName) => seriesName + ":",
          },
        },
        z: {
          formatter: undefined,
          title: "Size: ",
        },
        items: {
          display: "flex",
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        show: false,
      },
    },
  }

  const [chart, setChart] = React.useState(chartOptions)
  const [chartRawData, setChartRawData] = React.useState("")
  const [calibrationSessionData, setCalibrationSessionData] = React.useState("")
  const [ratingDecimal, setRatingDecimal] = React.useState(2)
  const [chartActual, setChartActual] = React.useState({ number: [], percent: [] })
  const [chartGuideline, setChartGuideline] = React.useState({ number: [], percent: [] })
  const [chartCategories, setChartCategories] = React.useState([])
  const [selectedChartType, setSelectedChartType] = React.useState("")
  const [action, setAction] = React.useState("")
  const [distributionMasterData, setDistributionMasterData] = React.useState([])
  const [distributionData, setDistributionData] = React.useState([])
  const [dataModified, setDataModified] = React.useState(false)
  const [customizeData, setCustomizeData] = React.useState({})
  const [scoreType, setScoreType] = React.useState(SCORETYPE.RATING)

  const checkIsDataModified = () => {
    let result = false
    // start compare
    distributionMasterData.forEach(master => {
      distributionData.forEach(current => {
        // loop dist
        if (master.serial === current.serial) {
          // compare length
          if (master.employee.length !== current.employee.length) {
            result = true
          } else {
            master.employee.forEach(mEmp => {
              let findEmp = current.employee.find(c => c.serial === mEmp.serial)
              if (!findEmp) {
                result = true
              }
            })
          }
        }
      })
    })

    if (!result && selectedVersion) {
      result = !selectedVersion.is_current
    }

    setDataModified(result)
  }

  const formatChartData = (data) => {
    let chartSeries = [{
      name: t("calibration.label.actual"),
      type: 'column',
      data: [0]
    }, {
      name: t("calibration.label.guideline"),
      type: 'line',
      data: [0]
    }]
    let totalActual = 0
    let tmpActualNumber = []
    let tmpActualPercent = []
    let tmpGuidelineNumber = []
    let tmpGuidelinePercent = []
    let tmpChartCategories = []
    data.forEach((item, index) => {
      tmpActualNumber.push(parseFloat(item?.actual) ?? 0)
      tmpGuidelineNumber.push(parseFloat(item?.guideline?.number) ?? 0)
      tmpGuidelinePercent.push(item?.guideline?.percent ?? 0)
      tmpChartCategories.push(`${index + 1} - ${item?.distribution ?? ""}`)
    })
    // cal actual percentage
    totalActual = tmpActualNumber.reduce((a, b) => a + b, 0)
    tmpActualNumber.forEach(actual => {
      tmpActualPercent.push(!isNaN(Math.round((actual / totalActual) * 100)) ? Math.round((actual / totalActual) * 100) : 0)
    })
    let totalActualPercent = 0
    totalActualPercent = tmpActualPercent.reduce((a, b) => a + b, 0)
    if (totalActualPercent > 100) {
      let exceedValue = totalActualPercent - 100
      for (let i = 0; exceedValue > 0; i++) {
        if (tmpActualPercent[i] > 0) {
          tmpActualPercent[i] = tmpActualPercent[i] - 1 // remove exceed 100 percent
          exceedValue--
        }
      }
    }

    setChartActual({ percent: tmpActualPercent, number: tmpActualNumber })
    setChartGuideline({
      number: tmpGuidelineNumber,
      percent: tmpGuidelinePercent
    })

    // do set chart data
    let tmpChartData = {
      ...chart,
      ...{
        series: chartSeries
      }
    }
    setChartCategories(tmpChartCategories)
    tmpChartData.options = {
      ...tmpChartData.options,
      ...{
        xaxis: {
          ...tmpChartData.options.xaxis,
          categories: tmpChartCategories,
        },
      },
    }
    updatedChart(tmpChartData)
  }

  const handleDistributionData = (data) => {
    let distFormat = []
    Object.entries(data).forEach(([key, item]) => {
      let tmp = {
        serial: "",
        seq: key,
        name: "",
        guideline: "",
        employee: [{
          serial: "",
          name: "",
          score: "",
          rating: "",
          calibration: ""
        }]
      }
      tmp.serial = item?.distribution?.id ?? ""
      tmp.name = item?.distribution?.description ?? ""
      tmp.guideline = item?.guideline ?? 0
      tmp.employee = item?.calibration_employee ? item?.calibration_employee?.map(emp => {
        let splitName = emp?.employee?.fullname.split(" ")
        return {
          serial: emp?.employee?.serial,
          fName: splitName[0],
          lName: splitName[1],
          score: emp?.score,
          rating: emp?.rating_classify,
          calibration: emp?.calibration_classify,
          form_status: emp?.employee?.form_status,
          evaluator_grade: emp?.evaluator_grade,
        }
      }) : []
      distFormat.push(tmp)
    })
    // do move `no score` to last position
    distFormat.push(distFormat.shift())

    setDistributionData(distFormat)
    setDistributionMasterData(distFormat)
  }


  const isSaveBtnDisabled = () => {
    if (action?.edit) {
      if (dataModified)
        return false
    }
    return true
  }

  const isSubmitBtnDisabled = () => {
    if (action?.submit) {
      if (!dataModified)
        return false
    }
    return true
  }

  const updatedChart = (tmpChartData = null) => {
    if (!tmpChartData) tmpChartData = { ...chart }
    if (selectedChartType === "number") {
      tmpChartData = {
        ...tmpChartData,
        ...{
          series: [{
            name: t("calibration.label.actual"),
            type: 'column',
            data: chartActual?.number ?? [0]
          }, {
            name: t("calibration.label.guideline"),
            type: 'line',
            data: chartGuideline?.number ?? [0]
          }]
        }
      }
      tmpChartData.options = {
        ...tmpChartData.options,
        ...{
          xaxis: {
            ...tmpChartData.options.xaxis,
            categories: chartCategories,
          },
          yaxis: [{
            ...tmpChartData.options.yaxis,
            axisBorder: {
              show: true,
              color: '#AAAAAA',
              width: 2,
              offsetX: 3,
              offsetY: 0
            },
            labels: {
              ...tmpChartData.options.yaxis.labels,
              formatter: (value) => { return `${value}` }
            }
          }],
        },
      }
      setChart(tmpChartData)
    } else if (selectedChartType === "percentage") {
      tmpChartData = {
        ...tmpChartData,
        ...{
          series: [{
            name: t("calibration.label.actual"),
            type: 'column',
            data: chartActual?.percent ?? [0]
          }, {
            name: t("calibration.label.guideline"),
            type: 'line',
            data: chartGuideline?.percent ?? [0]
          }]
        }
      }
      tmpChartData.options = {
        ...tmpChartData.options,
        ...{
          xaxis: {
            ...tmpChartData.options.xaxis,
            categories: chartCategories,
          },
          yaxis: [{
            ...tmpChartData.options.yaxis,
            axisBorder: {
              show: true,
              color: '#AAAAAA',
              width: 2,
              offsetX: 3,
              offsetY: 0
            },
            labels: {
              ...tmpChartData.options.yaxis.labels,
              formatter: (value) => { return `${value}%` }
            }
          }],
        },
      }
      setChart(tmpChartData)
    }
  }

  const changeCalibrationTableEmployee = () => {
    let changedData = { ...calibrationState.changedCalibrationData }
    let originalData = JSON.parse(JSON.stringify(distributionData)) //deep copy
    let resultData = originalData

    let findEmp = null
    let findNewDist = null
    let newDistIndex = null
    Object.entries(changedData).forEach(([key, value]) => {
      resultData = originalData.map(dist => {
        findNewDist = null
        newDistIndex = null
        // find emp
        findEmp = dist.employee.find(emp => emp.serial === key)
        if (findEmp) {
          // find destination dist
          if (dist.serial !== value) {
            findNewDist = originalData.find(newDist => newDist.serial === value)
            newDistIndex = originalData.findIndex(newDist => newDist.serial === value)
            // case changed : find & remove on old dist
            dist.employee = dist.employee.filter(remove => remove.serial !== key)
            // prepare employee to new dist data
            if (findNewDist && newDistIndex > -1) {
              findNewDist.employee = [
                ...findNewDist.employee,
                ...[
                  {
                    ...findEmp,
                    calibration: {
                      id: findNewDist.serial,
                      seq: parseInt(findNewDist.seq)
                    }
                  }
                ]
              ]
              // set employee to new dist data
              resultData[newDistIndex] = findNewDist
            }
          }
        }
        return dist
      })
    })
    setDistributionData(resultData)
  }

  const fetchAndSetData = (save = false) => {
    dispatch(getCalibrationDetail(serial ?? "", selectedVersion?.id ?? "")).then(response => {
      const resp = response.data

      let summary = resp?.calibration_summary
      let cycle = resp?.cycle
      let session = resp?.calibration_session
      let chart = resp?.calibration_graph
      let distribution = resp?.calibration_distribution
      let action = resp?.action
      let versions = resp?.versions || []
      let thisVersion = resp?.view_version ? { id: resp?.view_version } : find(versions, { is_current: true })
      if (save) thisVersion = find(versions, { is_current: true })

      if (response?.custom) {
        setCustomizeData(response?.custom)
      }

      setAction(action)
      handleDistributionData(distribution)
      setSummaryData({ total: summary?.total ?? 0, withRating: summary?.with_rating ?? 0, noRating: summary?.no_rating })
      setCycleData(cycle)
      setChartRawData(chart)
      setCalibrationSessionData(session)
      dispatch({
        type: "SET_RATE_DECIMAL",
        payload: session?.rate_decimal ?? 2
      })
      setRatingDecimal(session?.rate_decimal ?? 2)
      setSelectedChartType("number")
      setVersions(versions)
      //setSelectedVersion(null)
      setSelectedCurrentVersion(thisVersion ?? null)

    })
  }

  const downloadData = () => {
    dispatch(downloadCalibrationData(serial)).then((resp) => {
      window.location = resp.file
    })
  }

  const closeUpload = (refresh = false) => {
    setOpenUpload(false)
    if (refresh) fetchAndSetData()
  }

  // Approve / Reject


  const [messageSuccess, setMessageSuccess] = React.useState("")
  const [failMessage, setMessageFail] = React.useState("")
  // message dialog
  const [openSucceedMessageDialog, setSucceedMessageDialog] =
    React.useState(false)
  const handleOpenSucceedMessageDialog = (value) => {
    setSucceedMessageDialog(true)
  }
  const handleCloseSucceedMessageDialog = (value) => {
    setSucceedMessageDialog(false)
    window.location.reload()
    //navigate(`/kpi/inbox`, { replace: false })
  }
  const [openFailedMessageDialog, setFailedMessageDialog] =
    React.useState(false)
  const handleOpenFailedMessageDialog = () => {
    setFailedMessageDialog(true)
  }
  const handleCloseFailedMessageDialog = () => {
    setFailedMessageDialog(false);
    window.location.reload()
    //navigate(`/kpi/inbox`, { replace: false })
  }

  const [openApproveConfirmDialog, setApproveConfirmDialog] =
    React.useState(false)
  const handleOpenApproveConfirmDialog = () => {
    setApproveConfirmDialog(true)
  }
  const handleCloseApproveConfirmDialog = () => {
    setApproveConfirmDialog(false)
  }
  const handleApproval = (value) => {
    let approveData = {
      calibration_session_id: serial ?? "",
      remark: value.description,
    }
    dispatch(postWorkflowSubmit(approveData))
      .then(() => {
        fetchAndSetData()
        setApproveConfirmDialog(false);
        setMessageSuccess(
          t("calibration.label.dialog.approvalSuccess")
        )
        handleOpenSucceedMessageDialog(true);
      })
      .catch(() => {
        setApproveConfirmDialog(false);
        setMessageFail(
          t("calibration.label.dialog.approvalFail")
        )
        handleOpenFailedMessageDialog(true);
      })
  }
  const [openRejectConfirmDialog, setRejectConfirmDialog] =
    React.useState(false);
  const handleOpenRejectConfirmDialog = (value) => {
    setRejectConfirmDialog(true)
  }
  const handleCloseRejectConfirmDialog = (value) => {
    setRejectConfirmDialog(false)
  }
  const handleReject = (value) => {
    let rejectData = {
      calibration_session_id: serial ?? "",
      remark: value.description,
    };
    dispatch(postWorkflowReject(rejectData))
      .then(() => {
        fetchAndSetData()
        setRejectConfirmDialog(false);
        setMessageSuccess(
          t("calibration.label.dialog.rejectSuccess")
        )
        handleOpenSucceedMessageDialog(true);
      })
      .catch(() => {
        setRejectConfirmDialog(false);
        setMessageFail(
          t("calibration.label.dialog.rejectFail")
        )
        handleOpenFailedMessageDialog(true);
      })
  }

  // --------------------------------------------------------------------------------- useEffect here ---------------------------------------------------------------------------------
  useEffect(() => {
    fetchAndSetData()
    return () => {
      dispatch({
        type: "CLEAR_CALIBRATION_DATA"
      })
    }
  }, [])

  useEffect(() => {
    changeCalibrationTableEmployee()
  }, [calibrationState])

  useEffect(() => {
    updatedChart()
  }, [selectedChartType, chartActual, chartGuideline, chartCategories])

  // set chart data
  useEffect(() => {
    let tmpChartData = [...chartRawData]
    let tmpDistData = [...distributionData]
    tmpDistData.forEach(dist => {
      let findChartIndex = tmpChartData.findIndex(chartItem => chartItem.distribution === dist.name)
      if (findChartIndex > -1) {
        if (tmpChartData?.[findChartIndex]) {
          tmpChartData[findChartIndex].actual = dist?.employee?.length ?? 0
        }
      }
    })
    formatChartData(tmpChartData)
  }, [distributionData, chartRawData])

  useEffect(() => {
    checkIsDataModified()
  }, [distributionData, distributionMasterData])


  useEffect(() => {

    if (selectedVersion && selectedCurrentVersion && selectedVersion.id !== selectedCurrentVersion.id) {
      fetchAndSetData()
    }

  }, [selectedVersion])

  useEffect(() => {
    let type = SCORETYPE.RATING
    if (customizeData?.display) {
      if (customizeData.display === 'percentage_weighted') type = SCORETYPE.PERCENTAGE
    }
    setScoreType(type)
  }, [customizeData])

  // --------------------------------------------------------------------------------- end useEffect ---------------------------------------------------------------------------------

  return (
    <>
      <Helmet>
        <title>Calibration | PMS </title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Grid container item py={2}>
            <Typography fontSize={16} lineHeight={1} color="#68717A">
              <span
                style={styles.link}
                onClick={() => goTo("/kpi/home")}
              >
                {t("label.home")}
              </span>{" "}
              /
              <span
                style={styles.link}
                onClick={() => goTo("/kpi/calibration")}
              >
                {" "} {t("calibration.label.title")}
              </span>{" "}
              / <span style={styles.end}> {calibrationSessionData?.name ?? "-"}  </span>
            </Typography>
          </Grid>
          <Grid container xs={12} mt={2} mb={3}>
            <Grid item display="flex">
              <Typography
                variant="h1"
                fontSize={36}
                lineHeight="39.6px"
                fontWeight="500"
                color="#404349"
                sx={{ mr: "45px" }}
              >
                {calibrationSessionData?.name ?? "-"}
              </Typography>
              {statusBox(calibrationSessionData?.status)}
            </Grid>
            <Grid item ml="auto">
              <Box sx={{ height: "45px", background: "#FFF", border: "1px solid #E4E4E4", borderRadius: "4px", p: "10px", display: "flex", alignItems: "center" }}>
                <Typography fontSize="16px" fontWeight="500" color="#404349">{cycleData?.name ?? "-"}</Typography>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <Paper sx={{ height: "140px", display: "flex", alignItems: "center", padding: "20px" }} classes={{ root: classes.boxStyled }}>
                <Grid container spacing={2}>
                  <Grid
                    item
                    display="flex"
                    alignItems="center"
                    xs={4}
                  >
                    <PeopleGroupIcon className={classes.icon} />
                  </Grid>
                  <Grid
                    item
                    xs={8}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Grid container item mb={0.5}>
                      <Typography fontWeight="600" fontSize="48px" lineHeight={1}>
                        {summaryData?.total ?? 0}
                      </Typography>
                    </Grid>
                    <Grid container item mt={0.5}>
                      <Typography fontWeight="400" fontSize="14px" color="#AAAAAA">
                        {t("calibration.label.totalEmployee")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ height: "140px", display: "flex", alignItems: "center", padding: "20px", maxWidth: "100%" }} classes={{ root: classes.boxStyled }}>
                <Grid container spacing={2}>
                  <Grid
                    item
                    display="flex"
                    alignItems="center"
                    xs={4}
                  >
                    <StarsGroupIcon className={classes.icon} />
                  </Grid>
                  <Grid
                    item
                    xs={8}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Grid container item mb={0.5}>
                      <Typography fontWeight="600" fontSize="48px" lineHeight={1}>
                        {summaryData?.withRating ?? 0}
                      </Typography>
                    </Grid>
                    <Grid container item mt={0.5}>
                      <Typography fontWeight="400" fontSize="14px" color="#AAAAAA">
                        {t("calibration.label.totalEmployeeWithScore")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ height: "140px", display: "flex", alignItems: "center", padding: "20px" }} classes={{ root: classes.boxStyled }}>
                <Grid container spacing={2}>
                  <Grid
                    item
                    display="flex"
                    alignItems="center"
                    xs={4}
                  >
                    <StarsGroupInactiveIcon className={classes.icon} />
                  </Grid>
                  <Grid
                    item
                    xs={8}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Grid container item mb={0.5}>
                      <Typography fontWeight="600" fontSize="48px" lineHeight={1}>
                        {summaryData?.noRating ?? 0}
                      </Typography>
                    </Grid>
                    <Grid container item mt={0.5}>
                      <Typography fontWeight="400" fontSize="14px" color="#AAAAAA">
                        {t("calibration.label.noScore")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={4} mt={1}>
            <Grid item lg={12} md={12}>
              <Card classes={{ root: classes.boxStyled }}>
                <CardContent>
                  <Grid container spacing={1} p={1}>
                    <Grid item>
                      <Typography fontSize="24px" fontWeight="600" color="#404349">
                        {t("calibration.label.summary")}
                      </Typography>
                    </Grid>
                    <Grid item xs="auto" display="flex" alignItems="center" ml="auto">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ width: "20px", height: "20px", background: cssVariables.color.primary, mr: 1 }} />
                        <Typography fontSize="16px" fontWeight="400" color="#AAA">{` = ${t("calibration.label.actual")}`}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                        <Box sx={{ width: "50px", position: "relative" }}>
                          <Box sx={{ width: "20px", height: "20px", background: "#F19562", borderRadius: "50%", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
                          <Box sx={{ width: "40px", border: "1px solid #F19562", background: "#F19562", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
                        </Box>
                        <Typography fontSize="16px" fontWeight="400" color="#AAA">{` = ${t("calibration.label.guideline")}`}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs="auto" ml={1}>
                      <Select
                        id="chartt-type-select"
                        inputlabelprops={{ shrink: false }}
                        variant="outlined"
                        size="small"
                        className="white-bg"
                        sx={{ width: "200px" }}
                        value={selectedChartType}
                        onChange={e => setSelectedChartType(e.target.value)}
                      >
                        <MenuItem value={"number"} key={1}>
                          {t("calibration.label.noOfEmployee")}
                        </MenuItem>
                        <MenuItem value={"percentage"} key={2}>
                          {t("calibration.label.percentage")}
                        </MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12} mt={0}>
                      <Box
                        sx={{
                          ".apexcharts-tooltip-marker": { borderRadius: "2px" },
                          ".apexcharts-tooltip-y-group > span": {
                            fontWeight: "400",
                          },
                          ".apexcharts-tooltip": {
                            background: "#F7F7F7",
                            fontWeight: "400",
                          },
                          height: "300px",
                          ".apexcharts-legend-text": {
                            color: "#A3A3A3 !important",
                          },
                          ".apexcharts-text": {
                            color: "#A3A3A3 !important",
                            fill: "#A3A3A3  !important",
                          },
                          ".apexcharts-tooltip-title": {
                            background: "#5DA196 !important",
                            color: "#FFFFFF",
                            fontWeight: "400",
                          },
                        }}
                      >
                        <ApexCharts
                          options={chart.options}
                          series={chart.series}
                          type={chart.options.chart.type}
                          height="100%"
                        ></ApexCharts>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Grid container mt={4} mb="80px">
            <Grid item xs={12}>
              <Card classes={{ root: classes.boxStyled }}>
                <CardContent>
                  <Grid container display="flex" sx={{ minHeight: "45px" }}>
                    <Grid item xs={6} alignItems="center">
                      <Typography fontSize="24px" fontWeight="600" color="#404349">
                        {t("calibration.label.calibrationTable")}
                      </Typography>
                    </Grid>

                    <Grid item xs="auto" ml="auto" display="flex" alignItems="center">
                      <Typography fontSize="18px" fontWeight="400" color="#404349" mr={2}>
                        Version:
                      </Typography>


                      <Autocomplete
                        id="country-select-demo"
                        options={versions}
                        // disableClearable
                        defaultValue=""
                        displayEmpty
                        disableClearable
                        value={selectedCurrentVersion}
                        getOptionLabel={(option) => {
                          let label = ''
                          if (option?.name === undefined) {
                            if (option?.id) {
                              let opt = find(versions, { id: option.id })
                              if (opt) label = opt.is_current ? t('calibration.label.lastUpdate') : opt.name
                            }
                          } else label = option?.is_current ? t('calibration.label.lastUpdate') : option.name

                          return label
                        }}
                        sx={{ width: "300px" }}
                        onChange={(event, newValue) => {
                          setSelectedVersion(newValue);
                        }}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} sx={{
                            position: "relative",
                            "::hover": {
                              background: "#000 !important"
                            },
                            "&:hover": { cursor: "pointer", background: "#5DA196 !important", ".MuiTypography-root": { color: "#fff !important" } }

                          }}>
                            <Box >
                              <Typography fontWeight="500" fontSize={18} lineHeight="26.91px" color="#404349" sx={{ width: "100%", "white-space": "nowrap", overflow: "hidden", "text-overflow": "ellipsis" }}>{option?.is_current ? t('calibration.label.lastUpdate') : option?.name}</Typography>
                              {option?.is_current &&
                                <Typography fontWeight="400" fontSize={14} lineHeight="20.93px" color="#404349" sx={{ width: "100%", "white-space": "nowrap", overflow: "hidden", "text-overflow": "ellipsis" }}>{option?.name}</Typography>
                              }
                              <Typography fontWeight="400" fontSize={14} lineHeight="20.93px" color="#404349" sx={{ width: "100%", "white-space": "nowrap", overflow: "hidden", "text-overflow": "ellipsis" }}>{option?.by}</Typography>
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            defaultValue=""
                            displayEmpty
                            size="small"
                            placeholder={'Select version'}
                            inputProps={{
                              ...params.inputProps,
                              readOnly: true,
                              autoComplete: 'new-password', // disable autocomplete and autofill
                            }}
                          />
                        )}
                      />

                      {/*
                      <Select
                        inputlabelprops={{ shrink: false }}
                        variant="outlined"
                        size="small"
                        className="white-bg"
                        value={selectedCurrentVersion}
                        onChange={e => setSelectedVersion(e.target.value)}
                      >
                        {versions.map((version, i) =>
                          <MenuItem value={version.id} key={i}>{version.is_current ? t('calibration.label.lastUpdate') : ''} {version.name}</MenuItem>
                        )}
                      </Select>
                      */}

                      <BootstrapTooltip title={'Download'} placement="top"><Button onClick={downloadData} sx={{ display: "flex", textTransform: "none", marginLeft: "10px" }} variant="dashed-solid" ><Download className={classes.primaryColorSvg} /></Button></BootstrapTooltip>

                      {(action?.edit || action?.approve) &&
                        <BootstrapTooltip title={'Import'} placement="top"><Button onClick={() => { setOpenUpload(true) }} sx={{ display: "flex", textTransform: "none", marginLeft: "10px" }} variant="dashed-solid" ><ImportIcon /></Button></BootstrapTooltip>
                      }

                    </Grid>

                  </Grid>
                  {/* calibration table */}
                  <Grid container mt={0}>
                    <Grid
                      item xs={12}
                      sx={{ overflow: "auto", py: 3 }}
                      display="flex"
                      id="calibration-table"
                    >
                      {/* calibration card */}
                      {
                        distributionData?.length > 0 && distributionData?.map((card, index) => {
                          return (
                            <Paper classes={{ root: classes.calibrationCardRoot }} sx={{ background: card?.serial !== "" && ((parseInt(card?.guideline) - parseInt(card?.employee?.length) > 0) || (parseInt(card?.employee?.length) - parseInt(card?.guideline) > 0)) ? "#F7E7E6" : card?.serial !== "" ? "#EFF6F4" : "#F6F6F6", mr: index < distributionData?.length - 1 ? "30px" : "0" }} key={`dist-${index}`}>
                              <Grid container mb="20px">
                                <Grid item display="flex" alignItems="center">
                                  <Box sx={{ maxWidth: "105px", mr: 0.5 }}>
                                    <Typography color="#404349" fontWeight="500" fontSize="18px" sx={{ "whiteSpace": "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {card?.seq > 0 && `${card?.seq} - `} {card?.name ?? ""}
                                    </Typography>
                                  </Box>
                                  <BootstrapTooltip
                                    title={t("calibration.label.actual")}
                                    placement="top"
                                  >
                                    <Box sx={{ width: "25px", height: "20px", background: "#404349", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                                      <Typography color="#FFF" fontWeight="500" fontSize="14px" lineHeight={1}>
                                        {card?.employee?.length ?? 0}
                                      </Typography>
                                    </Box>
                                  </BootstrapTooltip>
                                </Grid>
                                <Grid item ml="auto" display="flex" alignItems="center">
                                  <BootstrapTooltip
                                    title={t("calibration.label.guideline")}
                                    placement="top"
                                  >
                                    <DartsIcon />
                                  </BootstrapTooltip>
                                  <Typography color="#404349" fontWeight="500" fontSize="16px" lineHeight={1} sx={{ ml: 1 }}>
                                    {card?.guideline ?? 0}
                                  </Typography>
                                </Grid>
                              </Grid>
                              {
                                card?.serial !== "" && (
                                  card?.employee?.length < card?.guideline ?
                                    <Grid container mb="20px">
                                      <Grid item xs={12}>
                                        <Paper classes={{ root: classes.calibrationAlert }}>
                                          <CalibrationAlertIcon />
                                          <Typography color="#FFF" fontWeight="500" fontSize="14px" lineHeight={1} sx={{ ml: 1 }}>
                                            {t("calibration.label.pleaseAdd")} {parseInt(card?.guideline) - parseInt(card?.employee?.length)} {parseInt(card?.guideline) - parseInt(card?.employee?.length) > 1 ? t("calibration.label.members") : t("calibration.label.member")}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    </Grid>
                                    :
                                    card?.employee?.length > card?.guideline &&
                                    <Grid container mb="20px">
                                      <Grid item xs={12}>
                                        <Paper classes={{ root: classes.calibrationAlert }}>
                                          <CalibrationAlertIcon />
                                          <Typography color="#FFF" fontWeight="500" fontSize="14px" lineHeight={1} sx={{ ml: 1 }}>
                                            {t("calibration.label.pleaseRemove")} {parseInt(card?.employee?.length) - parseInt(card?.guideline)} {parseInt(card?.employee?.length) - parseInt(card?.guideline) > 1 ? t("calibration.label.members") : t("calibration.label.member")}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    </Grid>
                                )
                              }
                              {/* employee card */}
                              <Grid container item>
                                {
                                  card?.employee?.map((employee, index) => {
                                    let score = isNaN(parseFloat(employee?.score?.[Object.keys(employee?.score ?? {})?.[0]]).toFixed(ratingDecimal)) ? "-" : parseFloat(employee?.score?.[Object.keys(employee?.score ?? {})?.[0]]).toFixed(ratingDecimal)
                                    if (scoreType === SCORETYPE.PERCENTAGE) score += '%'
                                    return (
                                      <Grid container mb="20px" onClick={() => handleDetailDialog(employee, true)} className="child-calibration-table" key={`emp-${index}`}>
                                        <Grid item xs={12} classes={{ root: classes.calibrationItemGrid }}>
                                          <Paper classes={{ root: classes.calibrationItemCard }}>
                                            <Grid container display="flex" mb="8px">
                                              <Grid item xs={11}>
                                                <Typography color={cssVariables.color.primary} fontWeight="600" fontSize="16px" lineHeight={1.5} sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                                                  {employee?.fName ?? "-"}
                                                </Typography>
                                                <Typography color={cssVariables.color.primary} fontWeight="600" fontSize="16px" lineHeight={1.5} sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                                                  {employee?.lName ?? "-"}
                                                </Typography>
                                              </Grid>
                                              <Grid item ml="auto">
                                                <BootstrapTooltip
                                                  title={t("calibration.label.viewDetails")}
                                                  placement="top"
                                                >
                                                  <DocumentIcon />
                                                </BootstrapTooltip>
                                              </Grid>
                                            </Grid>
                                            <Grid container display="flex" mb="4px" alignItems="center">
                                              <Grid item xs={scoreType !== SCORETYPE.PERCENTAGE ? 6 : 12}>
                                                <Typography color="#AAAAAA" fontWeight="400" fontSize="14px" lineHeight={1.5} sx={{ textTransform: "capitalize" }}>
                                                  {t("calibration.label.score")}
                                                </Typography>
                                              </Grid>

                                              {scoreType !== SCORETYPE.PERCENTAGE &&
                                                <Grid item xs={6} display="flex" justifyContent="space-between" alignItems="center">
                                                  <Typography color="#AAAAAA" fontWeight="400" fontSize="14px" lineHeight={1.5}>
                                                    {t("calibration.label.rating")}:
                                                  </Typography>
                                                  <Typography color="#404349" fontWeight="400" fontSize="16px" lineHeight={1.5} sx={{ pr: "13px" }}>
                                                    {employee?.rating?.seq ?? 0}
                                                  </Typography>
                                                </Grid>
                                              }


                                            </Grid>


                                            <Grid container display="flex">

                                              <Grid item xs={6} display="flex" alignItems="center">
                                                {scoreType !== SCORETYPE.PERCENTAGE && <StarIcon className={classes.starIconSvg} />}
                                                <Typography color="#404349" fontWeight="400" fontSize="16px" lineHeight={1.5} sx={{ ml: 1 }}>
                                                  {score}
                                                </Typography>
                                              </Grid>

                                              {
                                                (employee?.rating?.seq ?? 0) !== (employee?.calibration?.seq ?? 0) &&
                                                <Grid item xs={6} display="none" justifyContent="space-between" alignItems="center">
                                                  <Typography color="#AAAAAA" fontWeight="400" fontSize="14px" lineHeight={1.5} sx={{}}>
                                                    {t("calibration.label.calibration")}:
                                                  </Typography>
                                                  <Typography color="#4085C9" fontWeight="400" fontSize="16px" lineHeight={1.5} sx={{ pr: "13px" }}>
                                                    {employee?.calibration?.seq ?? 0}
                                                  </Typography>
                                                </Grid>
                                              }
                                            </Grid>
                                            {employee?.evaluator_grade &&
                                              <Grid container display="flex" mb="2px" alignItems="center">
                                                <Grid item display="flex" alignItems="center">
                                                  <Typography color="#AAAAAA" fontWeight="400" fontSize="14px" lineHeight={1.5}>Evaluator Grade:</Typography>
                                                  <Typography color="#404349" fontWeight="400" fontSize="16px" lineHeight={1.5} sx={{ ml: "13px" }}>
                                                    {employee?.evaluator_grade || '-'}
                                                  </Typography>
                                                </Grid>

                                              </Grid>
                                            }

                                            <Grid container display="flex" mt={1}>
                                              <Grid item xs={'auto'} display="flex" alignItems="center">
                                                <Box py="4px" px="8px" sx={{ "max-width": "100%", width: "max-content", border: employee?.form_status?.code === "not_started" ? "1px solid #DADADA" : "none", "border-radius": "5px", background: employee?.form_status?.code === "in_progress" ? "#EFB76D1A" : employee?.form_status?.code === "pending" ? "#FEF3EA" : employee?.form_status?.code === "completed" || employee?.form_status?.code === "approved" ? "#5DA1961A" : employee?.form_status?.code === "deleted" ? "#8282821A" : "1px solid #DADADA" }} >
                                                  <Typography fontSize={12} lineHeight="16px" fontWeight={400} color={employee?.form_status?.code === "in_progress" ? "#EFB76D" : employee?.form_status?.code === "pending" ? "#F28026" : employee?.form_status?.code === "completed" || employee?.form_status?.code === "approved" ? "#5DA196" : employee?.form_status?.code === "deleted" ? "#828282" : "#6B6B6B"} sx={{ "text-align": "center", "white-space": "nowrap", width: "100%", overflow: "hidden", "text-overflow": "ellipsis" }}>
                                                    {employee?.form_status.name}
                                                  </Typography>
                                                </Box>
                                              </Grid>
                                            </Grid>

                                          </Paper>
                                        </Grid>
                                      </Grid>
                                    )
                                  })
                                }
                              </Grid>
                            </Paper>
                          )
                        })
                      }
                      {/* end calibration card */}
                    </Grid>
                  </Grid>
                  {/* end calibration table */}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* calibration detail dialog */}
          <DetailDialog
            open={isDetailDialogOpen}
            setValue={() => setIsDetailDialogOpen(false)}
            detailData={selectedDetailEmployeeData}
            openConfirm={handleChangeCalibrationDialog}
            confirmChange={isConfirmChangeCalibration}
            canEdit={action?.edit}
            ratingDecimal={ratingDecimal}
            scoreType={scoreType}
          />
          {/* confirm change calibration rating on detail card */}
          <ConfirmDialog open={isOpenChangeCalibrationDialog} warning={true} doConfirm={(val) => handleConfirmChangeClassificationDropdown(val)} setValue={() => handleChangeCalibrationDialog(false)} title={t("calibration.label.dialog.changeConfirmTitle")} text={t("calibration.label.dialog.changeConfirmText")} />
          {/* confirm discard */}
          <ConfirmDialog open={isOpenDiscardDialog} warning={true} doConfirm={() => handleConfirmDiscard()} setValue={() => setIsOpenDiscardDialog(false)} title={t("calibration.label.dialog.discardTitle")} text={t("calibration.label.dialog.discardText")} />
          {/* confirm save */}
          <ConfirmDialog open={isOpenSaveDialog} warning={true} doConfirm={() => handleConfirmSave()} setValue={() => setIsOpenSaveDialog(false)} title={t("calibration.label.dialog.saveTitle")} text={t("calibration.label.dialog.saveText")} />
          {/* confirm submit */}
          <ConfirmDialog open={isOpenSubmitDialog} warning={true} doConfirm={() => handleConfirmSubmit()} setValue={() => setIsOpenSubmitDialog(false)} title={t("calibration.label.dialog.submitTitle")} text={`${t("calibration.label.dialog.submitText1")} ${calibrationSessionData?.name} ${t("calibration.label.dialog.submitText2")}`} />

          {/* message dialog */}
          <MessageDialog open={isOpenSuccessDialog} setValue={setIsOpenSuccessDialog} title={t("inbox.label.dialog.success")} text={successDialogMessage} />

        </Container>
        <Paper sx={{ width: "100%", height: "80px", position: "fixed", bottom: 0, boxShadow: "1px -4px 15px rgba(0, 0, 0, 0.1)", borderRadius: "10px 10px 0px 0px", display: "flex", alignItems: "center" }}>
          <Grid container display="flex" alignItems="center" my="17px" mx="18px">
            <Grid item xs="auto">
              <Button
                size="small"
                aria-controls="back-button"
                aria-haspopup="true"
                onClick={handleBack}
                color="black"
                variant="outlined"
                sx={{
                  minWidth: "111px",
                  height: "45px",
                  textTransform: "none",
                  p: 0,
                }}
                classes={{ disabled: classes.btnDisabled }}
              >
                <ArrowLeftIcon />
                <Typography fontSize="16px" fontWeight="500" sx={{ ml: 1 }}>
                  {t("label.back")}
                </Typography>
              </Button>
            </Grid>
            <Grid item xs="auto" ml="auto" display="flex" alignItems="center">
              {
                (calibrationSessionData?.updated_by || calibrationSessionData?.submit_by || calibrationSessionData?.proxy_by) &&
                <Typography fontSize="16px" fontWeight="400" sx={{ mr: 1 }} color="#AAAAAA">
                  {calibrationSessionData?.proxy_by ?
                    t("calibration.label.proxyBy")
                    : calibrationSessionData?.updated_by ? t("calibration.label.updatedBy")
                      : t("calibration.label.submittedBy")}:
                  {` `}
                  {calibrationSessionData?.proxy_by ? calibrationSessionData.proxy_by : calibrationSessionData?.updated_by ? calibrationSessionData?.updated_by : calibrationSessionData.submit_by}
                  {` `}
                  ({calibrationSessionData?.updated_at ? dateTimeFormat(calibrationSessionData.updated_at) : calibrationSessionData?.submit_at ? dateTimeFormat(calibrationSessionData.submit_at) : "-"})
                </Typography>
              }

              {action?.edit &&
                <Button
                  size="small"
                  aria-controls="save-form"
                  aria-haspopup="true"
                  onClick={() => setIsOpenSaveDialog(true)}
                  disabled={isSaveBtnDisabled()}
                  color="primary"
                  variant="contained"
                  sx={{
                    minWidth: "111px",
                    height: "45px",
                    textTransform: "none",
                    border: "none",
                    p: 0,
                    mr: 2
                  }}
                  classes={{ disabled: classes.btnDisabled }}
                >
                  <FloppyDiskIcon className={classes.primaryColorSvg} />
                  <Typography fontSize="16px" fontWeight="500" sx={{ ml: 1 }}>
                    {t("label.save")}
                  </Typography>
                </Button>
              }

              {action?.submit &&
                <Button
                  size="small"
                  aria-controls="submit-form"
                  aria-haspopup="true"
                  onClick={() => setIsOpenSubmitDialog(true)}
                  color="primary"
                  disabled={isSubmitBtnDisabled()}
                  variant="contained"
                  sx={{
                    minWidth: "111px",
                    height: "45px",
                    textTransform: "none",
                    border: "none",
                    p: 0,
                    ml: 2
                  }}
                  classes={{ disabled: classes.btnDisabled }}
                >
                  <CompassIcon className={classes.primaryColorSvg} />
                  <Typography fontSize="16px" fontWeight="500" sx={{ ml: 1 }}>
                    {t("calibration.label.submit")}
                  </Typography>
                </Button>
              }

              {action?.reject &&

                <Button
                  color="red"
                  variant="contained"
                  startIcon={<MarkIcon />}
                  sx={{ marginRight: 1, textTransform: "none", width: "139px", height: "45px" }}
                  onClick={handleOpenRejectConfirmDialog}
                >
                  <Typography fontSize="16px" fontWeight="600">{t("inbox.label.reject")}</Typography>
                </Button>
              }

              {
                action?.approve &&
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<CheckIcon />}
                  sx={{ textTransform: "none", width: "139px", height: "45px" }}
                  onClick={handleOpenApproveConfirmDialog}
                >
                  <Typography fontSize="16px" fontWeight="600">{t("inbox.label.approve")}</Typography>
                </Button>
              }

            </Grid>
          </Grid>
        </Paper>
      </Box>

      <UploadDialog id={serial} typeFiles={['xlsx']} open={openUpload} close={closeUpload} />


      <ConfirmInboxDialog
        open={openApproveConfirmDialog}
        action={handleApproval}
        setValue={handleCloseApproveConfirmDialog}
        type="approve"
        title={t("inbox.label.dialog.confirmApproval")}
        text={t("inbox.label.dialog.sureConfirmApproval")}
      />
      <ConfirmInboxDialog
        open={openRejectConfirmDialog}
        action={handleReject}
        setValue={handleCloseRejectConfirmDialog}
        type="reject"
        title={t("inbox.label.dialog.rejectApproval")}
        text={t("inbox.label.dialog.sureRejectApproval")}
      />
      <MessageDialog
        open={openFailedMessageDialog}
        setValue={handleCloseFailedMessageDialog}
        type="fail"
        title={t("inbox.label.dialog.fail")}
        text={failMessage}
      />
      <MessageDialog
        open={openSucceedMessageDialog}
        setValue={handleCloseSucceedMessageDialog}
        type="success"
        title={t("inbox.label.dialog.success")}
        text={messageSuccess}
      />


    </>
  )
}
export default EditCalibration
